import React, {useState, useEffect} from 'react'
import { AppDataService } from '../services/AppDataService';
import useInfoModal from '../hooks/useInfoModal';
import { useDispatch, useSelector } from 'react-redux';
import { MODAL_TYPE_ENUM } from '../enums/CommonEnums';
import { ContainerRegistrationTable } from '../components/ContainerRegistrationTable';
import SkeletonGroup from '../components/SkeletonGroup';
import { Button, Paper, Modal, Text, Center, Group, Stack } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import ContainerSubmissionForm from '../components/forms/ContainerSubmissionForm';
import FileDrop from '../components/FileDrop';
import { IconAlertCircle, IconAlertCircleFilled, IconBulb, IconFileDownload, IconFileUpload, IconInfoCircle, IconInfoSquare, IconNewSection } from '@tabler/icons-react';
import { RecentRegistrationsTable } from '../components/RecentRegistrationsTable';

const ContainerRegistrations = () => {
  const [loading, setLoading] = useState(false);
  const [registrations, setRegistrations] = useState([]);
  const [createSubmissionModalOpened, { open: openCreateSubmissionModal, close: closeCreateSubmissionModal }] = useDisclosure(false);
  const [submitFileModalOpened, { open: openSubmitFileModal, close: closeSubmitFileModal }] = useDisclosure(false);
  const authState = useSelector((state) => state.auth);
  const openModal = useInfoModal();

  useEffect(() => {
    fetchContainerRegistrations().catch(err => {
      setLoading(false);
      openModal(MODAL_TYPE_ENUM.GENERAL_FAILURE, err.message);
    })
  }, []);

  const fetchContainerRegistrations = async () => {
    setLoading(true);

    const response = await AppDataService.GetUserContainerRegistrations();

    if(Array.isArray(response?.data?.registrations)) {
      setRegistrations(response.data.registrations);
    }
    else {
      openModal(MODAL_TYPE_ENUM.GENERAL_FAILURE, 'Failed to fetch container registrations for user.');
    }

    setLoading(false);
  }

  const handleCreateSubmission = (records) => {
    closeCreateSubmissionModal();
     
    setRegistrations([...records, ...registrations])
  }

  const handleDeleteRow = async (id) => {
    const registrationsFiltered = registrations.filter(x => x.id !== id)

    await AppDataService.DeactivateUserContainerRegistration({ registrationId: id });

    setRegistrations(registrationsFiltered)
  }

  const handleEditRow = (record) => {
    const registrationsFiltered = registrations.filter(x => x.id !== record.id)
    registrationsFiltered.unshift(record);

    setRegistrations(registrationsFiltered)
  }

  const handleSubmitFile = async (file) => {
    try {
      var data = new FormData();

      data.append('FormFile', file);

      const response = await AppDataService.SubmitUserContainerRegistrationFile(data);
      const responseData = response.getResponse();

      if(response.hasError()) openModal(MODAL_TYPE_ENUM.REGISTRATION_FILE_FAILURE, responseData.data);
      else {
        openModal(MODAL_TYPE_ENUM.GENERAL_SUCCESS, 'Your submissions have been received!');

        const newRegistrations = responseData.data.registrations;

        setRegistrations([...newRegistrations, ...registrations]);
      } 
    }
    catch(e) {
      openModal(MODAL_TYPE_ENUM.GENERAL_FAILURE, e.message)
    }
  }

  const handleDownloadSubmissions = async (e) => {
    e.preventDefault();

    const response = await AppDataService.DownloadUserRegistrations();
  }

  return (
    <>
      <Modal size={1200} opened={createSubmissionModalOpened} onClose={closeCreateSubmissionModal} title="Create New Submission" centered>
        <Center>
          <ContainerSubmissionForm 
            onSubmit={(values) => handleCreateSubmission(values)}
          />
        </Center>
      </Modal>

      <Modal size={500} opened={submitFileModalOpened} onClose={closeSubmitFileModal} title="Create New Submission" centered>
        <Stack align='center'>
          <FileDrop 
            onDrop={(files) => {}}
            onUpload={(file) => handleSubmitFile(file)}
          />
        </Stack>
      </Modal>

      <Group mb={25}>
        <Button leftIcon={<IconNewSection size={15} />} disabled={loading} onClick={openCreateSubmissionModal}>Create New Submission</Button>
        <Button leftIcon={<IconFileDownload size={15} />} component='a' download href='https://obrc-cr.azurewebsites.net/OBRC%20Container%20Registration%20Template%20Data.xlsx' disabled={loading}>Download File Template</Button>
        <Button leftIcon={<IconFileUpload size={15} />} disabled={loading} onClick={openSubmitFileModal}>Upload Registration File</Button>
        <Button leftIcon={<IconFileDownload size={15} />}  disabled={loading} onClick={handleDownloadSubmissions}>Download Submissions</Button>
      </Group>
      {
        loading ? <SkeletonGroup height={600} width={1000} num={1} /> : 
        registrations.length > 0 ? <ContainerRegistrationTable tableData={registrations} onEdit={handleEditRow} onDelete={handleDeleteRow} /> : (
          <Paper shadow='md' withBorder radius='lg' maw={800} mx='auto' mt={50} p={50}>
            <Center>
              <IconBulb stroke={1.5} color='#228be6' size={100} />
            </Center>
            <Text fz={20} mt={25} ta='center'>No submissions found. To get started, for individual entries click “Create New Submission” or for a bulk file upload you can download a spreadsheet template at “Download File Template”, fill in the details, and then come back to this page to upload it at "Upload Registration File."</Text>
          </Paper>
        )
      }
    </>
  )
}

export default ContainerRegistrations;